import React, { useState, useEffect, useRef, useMemo } from "react";
import GeneralInformationForm from "./Components/GeneralInformation";
import ProgressStep from "./Components/ProgressStepper";
import EventDatePicker from "./Components/EventDatePicker";
import CategorySelection from "./Components/CategorySelection";
import ProductServiceSelection from "./Components/ProductServiceSelection";
import CartPage from "../Cart/CartPage";
import { useCart } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import RecruitmentModal from "./Components/RecruitmentModal";
import PageHeader from "../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";

const EventCreationWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const { t } = useTranslation();
  const { cart, clearCart } = useCart();
  const [warningMessage, setWarningMessage] = useState("");
  const [direction, setDirection] = useState(0);
  const [showRecruitmentModal, setShowRecruitmentModal] = useState(true);

  const steps = useMemo(
    () => [
      t("wizard.steps.info"),
      t("wizard.steps.dates"),
      t("wizard.steps.categories"),
      t("wizard.steps.productsServices"),
      t("wizard.steps.confirmation"),
    ],
    [t]
  );
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      if (cart.length > 0) {
        clearCart();
      }
      isFirstRender.current = false;
      return;
    }
  }, [cart]);

  const [eventData, setEventData] = useState({
    attendees: 0,
    budget: 0,
    location: "",
    contactPhone: "",
    selectedCategory: null,
    selectedProducts: [],
    selectedServices: [],
    dates: [{ date: "", time: "", expanded: true }],
  });

  const goToNextStep = () => {
    setCurrentStep((prev) => {
      const next = prev + 1;
      setMaxStepReached((prevMax) => Math.max(prevMax, next));
      return next;
    });
    setDirection(1);
    setWarningMessage("");
  };

  const goToPreviousStep = () => {
    setDirection(-1);
    setCurrentStep((prev) => prev - 1);
  };

  const updateEventData = (data) =>
    setEventData((prev) => ({ ...prev, ...data }));

  const returnToCategorySelection = () => {
    if (currentStep != 3) {
      setCurrentStep(3);
    }

    setEventData((prev) => ({
      ...prev,
      selectedProducts: [],
      selectedServices: [],
      selectedCategory: null,
    }));

    setTimeout(() => {
      setWarningMessage(t("errors.noProductOrServiceFound"));
      window.scrollTo({ top: 0, behavior: "instant" });
    }, 50);
  };

  const handleStepClick = (targetIndex) => {
    if (targetIndex > maxStepReached) return;

    const diff = targetIndex - currentStep;
    setDirection(diff > 0 ? 1 : -1);
    setCurrentStep(targetIndex);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-[#e2e8f0] px-4">
      <PageHeader
        title={t("homepage.heroSection.cta")}
        subtitle={t("homepage.heroSection.services.createEvent.description")}
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed={true}
        rounded={false}
        height="sm"
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      <div className="w-full  ">
        {showRecruitmentModal && (
          <RecruitmentModal onClose={() => setShowRecruitmentModal(false)} />
        )}

        {/* Barre de progression */}
        {warningMessage && (
          <div className="text-lg text-semiBold text-red-500 text-center mb-4">
            {warningMessage}
          </div>
        )}
        <div className="flex justify-center overflow-x-auto py-6 px-2 no-scrollbar">
          <div className="min-w-fit">
            <ProgressStep
              currentStep={currentStep}
              steps={steps}
              onStepClick={handleStepClick}
            />
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={`step-${currentStep}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Étape 1 : Informations générales */}
            {currentStep === 1 && (
              <div className="flex justify-center px-4 py-12">
                <div className="w-full max-w-xl">
                  <GeneralInformationForm
                    nextStep={goToNextStep}
                    updateEventData={updateEventData}
                    eventData={eventData}
                    goToPreviousStep={goToPreviousStep}
                  />
                </div>
              </div>
            )}

            {/* Étape 2 : Choix de dates */}
            {currentStep === 2 && (
              <div className="flex justify-center  py-12">
                <div className="w-full max-w-xl">
                  <EventDatePicker
                    nextStep={goToNextStep}
                    updateEventData={updateEventData}
                    eventData={eventData}
                    goToPreviousStep={goToPreviousStep}
                  ></EventDatePicker>
                </div>
              </div>
            )}

            {/* Étape 3 : Bundles */}
            {currentStep === 3 && (
              <div>
                <CategorySelection
                  nextStep={goToNextStep}
                  updateEventData={updateEventData}
                  eventData={eventData}
                  goToPreviousStep={goToPreviousStep}
                ></CategorySelection>
              </div>
            )}

            {/* Étape 4 : Confirmation */}
            {currentStep === 4 && (
              <div>
                <ProductServiceSelection
                  nextStep={goToNextStep}
                  updateEventData={updateEventData}
                  eventData={eventData}
                  goToPreviousStep={goToPreviousStep}
                  returnToCategorySelection={returnToCategorySelection}
                ></ProductServiceSelection>
              </div>
            )}
            {/* Étape 4 : Confirmation */}
            {currentStep === 5 && (
              <div>
                <CartPage
                  nextStep={goToNextStep}
                  updateEventData={updateEventData}
                  eventData={eventData}
                  goToPreviousStep={goToPreviousStep}
                ></CartPage>
              </div>
            )}

            {
              //<EventSummaryFooter eventData={eventData}></EventSummaryFooter>
            }
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default EventCreationWizard;
